/**
 * E2E test-mode helpers used by the SPA.
 *
 * Activated in two ways:
 *  - Build-time: `VITE_E2E_TEST_MODE=1` (set by `pnpm test:e2e`).
 *  - Runtime: presence of the `__onenext_e2e_user` cookie, which the
 *    Playwright helper sets before navigating.
 *
 * When active, the SPA:
 *   1. Skips the `RequireAuth` Clerk-session check and the landing-page
 *      redirect on `/`, so protected routes render without a real
 *      browser sign-in.
 *   2. Forwards the cookie value as the `x-e2e-user-id` header on every
 *      API call, so the backend's matching test-mode bypass picks the
 *      right tenant.
 *
 * The cookie is read on every call to keep things stateless — the
 * helper specs reset it between tests.
 */

const COOKIE_NAME = "__onenext_e2e_user";
const HEADER_NAME = "X-E2E-User-Id";

const ENV_FLAG =
  typeof import.meta !== "undefined" &&
  (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_E2E_TEST_MODE === "1";

export function isE2eBuild(): boolean {
  return ENV_FLAG;
}

export function readE2eCookieUserId(): string | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie || "";
  const parts = raw.split(/;\s*/);
  for (const p of parts) {
    const eq = p.indexOf("=");
    if (eq < 0) continue;
    const k = p.slice(0, eq);
    if (k === COOKIE_NAME) {
      const v = decodeURIComponent(p.slice(eq + 1));
      return v.length > 0 ? v : null;
    }
  }
  return null;
}

/**
 * The bypass is active iff the build-time flag is on AND a cookie is
 * present. Either alone is insufficient — the build flag prevents
 * production bundles from honouring the cookie at all, and the cookie
 * is per-tab/per-test so dev-mode browsing is unaffected.
 */
export function isE2eBypassActive(): boolean {
  return isE2eBuild() && readE2eCookieUserId() !== null;
}

export function getE2eHeaders(): Record<string, string> {
  if (!isE2eBuild()) return {};
  const id = readE2eCookieUserId();
  return id ? { [HEADER_NAME]: id } : {};
}
